import { useCallback, useEffect, useRef, useState } from 'react';

import {
  JourneyChanges,
  JourneySettings,
  JourneysPayload,
  LifecycleJourney,
  lifecycleAPI,
  lifecycleErrorMessage,
} from '@/services/lifecycleAPI';
import {
  LifecycleAction,
  lifecycleFailureOutcome,
  LifecycleSurface,
  trackLifecycleActionTaken,
  trackLifecycleFeatureUsed,
} from '@/util/vexo';

export const RELOAD_FAILED_MESSAGE =
  'Saved, but the page could not reload. Refresh to see the latest.';

export class ReloadFailedError extends Error {
  constructor() {
    super(RELOAD_FAILED_MESSAGE);
    this.name = 'ReloadFailedError';
  }
}

// Which control a settings write came from, read off the fields it sends
// (every form sends only its own fields).
export const settingsAction = (changes: JourneyChanges): LifecycleAction => {
  if ('mode' in changes) return 'mode_change';
  if ('clear_sms_pause' in changes) return 'sms_pause_clear';
  if ('enabled' in changes) return 'journey_toggle';
  if ('sms_enabled' in changes) return 'sms_toggle';
  if ('holdout_percent' in changes || 'high_demand_threshold' in changes)
    return 'journey_detail_save';
  if ('tax_guide_url' in changes) return 'tax_guide_save';
  if (
    'monthly_sms_budget_cents' in changes ||
    'sms_segment_cost_cents' in changes
  )
    return 'budget_save';
  return 'pilot_save';
};

// The saved row (graphy returns the whole settings row, as in the page load)
// over the page's copy. The stats and other fields stay from the page load.
const withSaved = (
  payload: JourneysPayload,
  key: string,
  saved: JourneySettings,
): JourneysPayload =>
  key === '_global'
    ? {
        ...payload,
        global: {
          ...payload.global,
          ...(saved as Partial<JourneysPayload['global']>),
        },
      }
    : {
        ...payload,
        journeys: payload.journeys.map((j) =>
          j.journey_key === key
            ? { ...j, ...(saved as Partial<LifecycleJourney>) }
            : j,
        ),
      };

// `surface` is the screen using the hook: the Overview or Engine settings.
const useJourneys = (surface: LifecycleSurface = 'settings') => {
  const [data, setData] = useState<JourneysPayload | null>(null);
  const dataRef = useRef<JourneysPayload | null>(null);
  dataRef.current = data;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async (refresh: boolean) => {
    setError(null);
    let failure: unknown = null;
    try {
      const payload = await lifecycleAPI.getJourneys(
        refresh ? { refresh: true } : {},
      );
      if (mounted.current) setData(payload);
    } catch (err) {
      failure = err ?? new Error('Could not load the journeys.');
      if (mounted.current) {
        setError(lifecycleErrorMessage(err, 'Could not load the journeys.'));
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
    return failure;
  }, []);

  useEffect(() => {
    mounted.current = true;
    load(false);
    return () => {
      mounted.current = false;
    };
  }, [load]);

  const reload = useCallback(() => {
    setLoading(true);
    return load(false);
  }, [load]);

  const refresh = useCallback(async () => {
    trackLifecycleFeatureUsed({ feature: 'stats_refresh', surface });
    const failure = await load(true);
    if (failure) throw failure;
  }, [load, surface]);

  const updateJourney = useCallback(
    async (
      key: string,
      { version: openedVersion, ...changes }: JourneyChanges,
    ) => {
      const event = {
        action: settingsAction(changes),
        surface,
        journey_key: key === '_global' ? null : key,
        fields: Object.keys(changes),
        to_mode: changes.mode ?? null,
      };
      // Send the version this page loaded, so a save over someone else's newer
      // save gets 409 instead of replacing it (a list such as the allowlist is
      // saved whole; bug hunt 19 BH-1908). On 409 the page reloads, so the
      // admin sees the other change before saving again. graphy requires the
      // version: a save without one gets 422 and saves nothing. A settings
      // dialog passes the version its values came from (SettingsDialog).
      const version =
        openedVersion ??
        (key === '_global'
          ? dataRef.current?.global.version
          : dataRef.current?.journeys.find((j) => j.journey_key === key)
              ?.version);
      let saved: JourneySettings;
      try {
        saved = await lifecycleAPI.updateJourney(key, { ...changes, version });
      } catch (err) {
        trackLifecycleActionTaken({
          ...event,
          outcome: lifecycleFailureOutcome(err),
        });
        if (
          (err as { response?: { status?: number } })?.response?.status === 409
        ) {
          await load(false);
        }
        throw err;
      }
      trackLifecycleActionTaken({ ...event, outcome: 'success' });
      // Keep the row graphy returned: when the reload below fails, the page
      // still held the old version, and the admin's next save got a 409
      // blaming someone else for their own change (review CD6-03). The values
      // too, not only the version: with the old allowlist beside the new
      // version, a dialog opened next saved the old list and graphy accepted
      // it, dropping the admin's own change unseen (bug hunt 22).
      if (saved?.version && mounted.current) {
        setData((prev) => prev && withSaved(prev, key, saved));
      }
      if (await load(false)) throw new ReloadFailedError();
    },
    [load, surface],
  );

  return { data, loading, error, reload, refresh, updateJourney };
};

export type JourneysState = ReturnType<typeof useJourneys>;

export default useJourneys;
